/** @jsx jsx */
import React from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import { object, string } from 'yup';
import axios from 'axios';
import { jsx } from '@emotion/core';
import compose from 'lodash/fp/compose';
import { withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { withNamespaces } from 'react-i18next';
import Cookies from 'universal-cookie';
import defaultsDeep from 'lodash/defaultsDeep';
import get from 'lodash/get';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import { Input, Checkbox, PhoneNumber } from '../../ui/Form/index';
import { Icon } from '../../ui/Icon';
import Spacer from '../../ui/Spacer';
import DotComponent from '../../ui/DotComponent';
import Select, { mapDropdownValues } from '../../ui/Form/Dropdown';
import createButtonCss from '../../ui/Button';
import CtaLink from '../../ui/CtaLink';
import ThankYouForm from '../ThankYouForm';
import { showElementCss } from '../../../utils/responsive';
import {
  modalFadeOutCss,
  modalFadeInCss,
  ANIMATION_LENGTH,
} from '../../../utils/animations';
import { Container, Row, Column } from '../../../styles/Grid';
import MediaQueries from '../../../styles/Grid/MediaQueries';
import {
  SOLUTIONS,
  SOLUTION_GROUPS,
  API,
  CLOUDINARY_BASE_VALUES,
  REG_ONLY_NUMBERS,
} from '../../../utils/constants';
import { FORM_COPY as dict } from '../../../utils/dictionarykeys';
import { setAntiForgeryToken } from '../../../utils/forms';
import LottieAnimation, {
  getAmountAnimationIdFromIndex,
  getTopicsAnimationIdFromIndex,
} from '../../ui/LottieAnimation';
import { SafePureComponent } from '../../../utils/SafePureComponent';
import { setDataLayerObj } from '../../../utils/analytics';
import { getContactLineDataFromContext } from '../ContactLineNumber';
import Picture from '../../ui/Picture';
import { Breakpoints } from '../../../styles/Grid/GridSizes';
import * as styles from './index.styles';

export const defaultData = {
  AntiForgery: {
    name: '',
    value: '',
  },
  CompanySizeOptions: [],
  ContactIcon: {
    value: {},
  },
  ContactPhrase: {
    value: '',
  },
  DomainCountryISOCode2: {
    value: '',
  },
  EnquiryHeadlineSubtitle: {
    value: '',
  },
  ForHowLongOptions: [],
  Headline: {
    value: '',
  },
  HowManyPeopleOptions: [],
  InformationPhrases: {
    value: [],
  },
  LocalPhoneNumber: {
    value: '',
  },
  LocationID: {
    value: '',
  },
  MoveInDateOptions: [],
  PhoneNumberOptions: [
    {
      fields: {
        CountryName: {
          value: '',
        },
        DialingCode: {
          value: '',
        },
        Flag: {
          value: '',
        },
        CountryISOCode3: {
          value: '',
        },
        CountryISOCode2: {
          value: '',
        },
      },
    },
  ],
  PrefilledSolutionId: {
    value: '',
  },
  PrivacyPolicyLink: {
    value: {
      href: '#',
      linktype: 'internal',
      text: 'Link',
      anchor: '',
      querystring: '',
      target: '',
      id: '',
    },
  },
  SolutionsDropdownOptions: [],
  ThankYouViewImage: {
    value: { alt: '', src: '' },
  },
};

const FORM_ID = 'contact_form';
const defaultHeadlineDictionary = 'EnquiryHeadline';
const defaultSubmitDictionary = 'GetInTouchCTA';

const phraseImageSize = {
  ...CLOUDINARY_BASE_VALUES,
  aspectRatio: '1:1',
  width: 18,
  height: 18,
};

const phraseImageSizes = {
  [Breakpoints.sm]: phraseImageSize,
  [Breakpoints.md]: phraseImageSize,
  [Breakpoints.lg]: phraseImageSize,
  [Breakpoints.xl]: phraseImageSize,
};

const contactImageSize = {
  ...CLOUDINARY_BASE_VALUES,
  aspectRatio: '1:1',
  width: 48,
  height: 48,
};

const contactImageSizes = {
  [Breakpoints.sm]: contactImageSize,
  [Breakpoints.md]: contactImageSize,
  [Breakpoints.lg]: contactImageSize,
  [Breakpoints.xl]: contactImageSize,
};

export const decorateRequiredPlaceholder = placeholder => `${placeholder} *`;

export const getSolutionPrefilledValues = (defaultFields, options) => {
  const solution = get(defaultFields, 'Solution', '');
  const prefilledValue = find(options, option => {
    const value = option.fields.Label.value;
    // TODO: go around site and validate implementations are using id and not
    // label, then update return statement.
    return value === solution || option.id === solution.toLowerCase();
  });

  return {
    label: get(prefilledValue, 'fields.Label.value', ''),
    value: get(prefilledValue, 'id', ''),
  };
};

export class Enquiry extends SafePureComponent {
  constructor(props) {
    super(props);
    this.phoneNumberRef = React.createRef();
    this.t = props.t;
    this.cookies = new Cookies();
    this.fields = defaultsDeep(props.fields, defaultData);
    this.contactLineData = getContactLineDataFromContext(props.sitecoreContext);
    this.shouldShowAnimation =
      get(props.fields, 'InformationPhrases.value', []).length === 0;

    this.state = {
      isDropDownOpen: false,
      isThankyouVisible: false,
      currentAnimationId: null,
      channelCookie: this.cookies.get('channel_attrib') || 'Organic',
    };
  }

  componentDidMount() {
    const { isModal } = this.props;
    const modalDatalayerObject = {
      formType: 'contact',
      activeFormIds: 'contact_form',
      event: 'modalOpened',
    };

    if (isModal) setDataLayerObj(modalDatalayerObject);

    setTimeout(() => {
      this.setState({
        currentAnimationId: getTopicsAnimationIdFromIndex(0),
      });
    }, ANIMATION_LENGTH._300MS);
  }

  onNumberOfPeopleDropdownChange = (_event, _value, index) => {
    const placeholderCount = 1;
    const nextAnimationId = getAmountAnimationIdFromIndex(
      index - placeholderCount,
    );

    this.setState({ currentAnimationId: nextAnimationId });
  };

  onSolutionsDropdownChange = (_event, _value, index) => {
    const placeholderCount = 1;
    const nextAnimationId = getTopicsAnimationIdFromIndex(
      index - placeholderCount,
    );
    this.setState({ currentAnimationId: nextAnimationId });
  };

  getPhoneParams(values) {
    const countryISOCode2 = get(
      this.phoneNumberRef,
      'current.state.countryData.CountryISOCode2',
      {},
    );
    return `countryISOCode2=${countryISOCode2}&phoneNumber=${values.PhoneNumber}`;
  }

  toggleDropDown = event => {
    event.preventDefault();

    this.setState({ isDropDownOpen: !this.state.isDropDownOpen });
  };

  renderFormHeadline(fields) {
    const { headlineDictionary } = this.props;
    return (
      <React.Fragment>
        {this.renderGoBackButton()}
        <h1 id="contact_form_headline" css={styles.headline}>
          {this.t(headlineDictionary || defaultHeadlineDictionary)}
          <DotComponent />
        </h1>
        <h4 css={styles.headlineSubtitle}>
          {get(fields, 'EnquiryHeadlineSubtitle.value', '')}
        </h4>
      </React.Fragment>
    );
  }

  renderGoBackButton() {
    if (this.props.hideGoBack) {
      return;
    }

    return (
      <div css={[styles.goBackButtonContainer]}>
        <button css={styles.goBackButton} onClick={this.goBack}>
          <Icon name="arrow-left" />
          <span css={styles.goBackButtonLabel}>{this.t('GoBackButton')}</span>
        </button>
      </div>
    );
  }

  goBack = () => {
    window.history.back();
  };

  shouldRender(values, group) {
    const { Solution } = values;

    switch (true) {
      case group === SOLUTION_GROUPS.MEETING_ROOM:
        if (Solution === SOLUTIONS.MEETING_ROOM_ID) return styles.removeElement;
      case group <= SOLUTION_GROUPS.VIRTUAL_OFFICE:
        if (Solution === SOLUTIONS.VIRTUAL_OFFICE_ID)
          return styles.removeElement;
      case group <= SOLUTION_GROUPS.OTHER:
        if (
          Solution === SOLUTIONS.MEMBERSHIP_ID ||
          Solution === SOLUTIONS.OTHER_ID
        )
          return styles.removeElement;
      default:
        return {};
    }
  }

  shouldRenderSecondForm(values) {
    return (
      this.state.isDropDownOpen &&
      values.Solution !== SOLUTIONS.MEMBERSHIP_ID &&
      values.Solution !== SOLUTIONS.OTHER_ID
    );
  }

  renderSecondForm(
    {
      MoveInDateOptions,
      HowManyPeopleOptions,
      ForHowLongOptions,
      CompanySizeOptions,
    },
    handleChange,
    values,
  ) {
    if (!this.shouldRenderSecondForm(values)) return;

    return (
      <div
        css={
          this.state.isDropDownOpen ? styles.activeDropDown : styles.dropDown
        }
      >
        {!isEmpty(MoveInDateOptions) && (
          <Select
            css={[
              styles.enquiryInput,
              this.shouldRender(values, SOLUTION_GROUPS.MEETING_ROOM),
            ]}
            {...styles.selectCssProps}
            id="MoveInDate"
            onChangeProp={handleChange}
            placeholder={this.t('MoveInDatePlaceholder')}
            options={mapDropdownValues(MoveInDateOptions)}
            defaultValue={values.MoveInDate}
          />
        )}
        {!isEmpty(HowManyPeopleOptions) && (
          <Select
            id="HowManyPeople"
            onChangeProp={(...args) => {
              this.onNumberOfPeopleDropdownChange(...args);
              handleChange(...args);
            }}
            css={[
              styles.halfSelect,
              styles.enquiryInput,
              this.shouldRender(values, SOLUTION_GROUPS.VIRTUAL_OFFICE),
            ]}
            {...styles.selectCssProps}
            placeholder={this.t('HowManyPeoplePlaceholder')}
            options={mapDropdownValues(HowManyPeopleOptions)}
            defaultValue={values.HowManyPeople}
          />
        )}
        {!isEmpty(ForHowLongOptions) && (
          <Select
            id="ForHowLong"
            onChangeProp={handleChange}
            css={[
              styles.enquiryInput,
              styles.halfSelect,
              this.shouldRender(values, SOLUTION_GROUPS.MEETING_ROOM),
            ]}
            {...styles.selectCssProps}
            placeholder={this.t('ForHowLongPlaceholder')}
            options={mapDropdownValues(ForHowLongOptions)}
          />
        )}
        <Input
          id="CompanyName"
          onChange={handleChange}
          css={[
            styles.halfSelect,
            styles.enquiryInput,
            this.shouldRender(values, SOLUTION_GROUPS.OTHER),
          ]}
          {...styles.inputCssProps}
          placeholder={this.t('CompanyNamePlaceholder')}
        />
        {!isEmpty(CompanySizeOptions) && (
          <Select
            id="CompanySize"
            onChangeProp={handleChange}
            css={[
              styles.halfSelect,
              styles.enquiryInput,
              this.shouldRender(values, SOLUTION_GROUPS.OTHER),
            ]}
            {...styles.selectCssProps}
            placeholder={this.t('CompanySizePlaceholder')}
            options={mapDropdownValues(CompanySizeOptions)}
          />
        )}
      </div>
    );
  }

  renderGeneralError(msg) {
    return (
      <div css={styles.generalError}>
        <Icon name="error" customCss={styles.errorIcon} />
        {msg}
      </div>
    );
  }

  getLocationID(LocationID) {
    const locationID = get(LocationID, 'value');

    return Boolean(locationID)
      ? locationID
      : get(window, 'sessionStorage.LocationID', '');
  }

  mapValues(values, { DomainCountryISOCode2, LocationID }) {
    const countryData = get(
      this.phoneNumberRef,
      'current.state.countryData',
      {},
    );

    return {
      ...values,
      ...countryData,
      Solution: get(values, 'Solution.value', values.Solution),
      ChannelAttrib: this.state.channelCookie,
      DomainCountryISOCode2: DomainCountryISOCode2.value,
      LocationID: this.getLocationID(LocationID),
    };
  }

  renderSeparatorColumn() {
    return (
      <Column
        css={[
          showElementCss(MediaQueries.lgAndUp, 'flex'),
          styles.verticalSeparatorContainer,
        ]}
        cols={{ lg: 2 }}
      >
        <div css={styles.verticalSeparator} />
      </Column>
    );
  }

  renderRightSection(fields) {
    if (this.shouldShowAnimation) {
      return this.renderAnimationSection(fields);
    }

    return this.renderContextualInformation(fields);
  }

  renderAnimationSection(fields) {
    const { currentAnimationId } = this.state;
    return (
      <React.Fragment>
        {this.renderSeparatorColumn()}
        <Column cols={{ sm: 6, lg: 4, xl: 4 }}>
          <div
            css={[
              styles.animationContainer,
              showElementCss(MediaQueries.lgAndUp),
            ]}
          >
            <LottieAnimation
              height={260}
              nextAnimationId={currentAnimationId}
            />
          </div>
          {this.renderPhoneNumberBlock(fields)}
        </Column>
      </React.Fragment>
    );
  }

  renderContextualInformation(fields) {
    return (
      <React.Fragment>
        {this.renderSeparatorColumn()}
        <Column cols={{ sm: 6, md: 8, lg: 4, xl: 4 }}>
          <div css={showElementCss(MediaQueries.lgAndUp)}>
            <h2 css={styles.contextualInfoTitle}>
              {get(fields, 'Headline.value', '')}
            </h2>
            <ul>
              {get(fields, 'InformationPhrases.value', []).map(
                ({ Phrase, Icon }, index) => (
                  <li css={styles.phrase} key={`phrase-${index}`}>
                    <Picture
                      imageCss={styles.phraseImage}
                      image={Icon}
                      imageSizes={phraseImageSizes}
                      instantLoad
                    />
                    <span css={styles.phraseLabel}>
                      {get(Phrase, 'value', '')}
                    </span>
                  </li>
                ),
              )}
            </ul>
          </div>
          {this.renderPhoneNumberBlock(fields)}
        </Column>
      </React.Fragment>
    );
  }

  renderPhoneNumberBlock(fields = {}) {
    const { contactLineData } = this;
    const { ContactIcon, ContactPhrase } = fields;
    const phone = get(contactLineData, 'fields.CountryPhone.value', '');

    return (
      <div css={styles.contactNumberBlock}>
        <div css={styles.contactNumberContent}>
          <div css={styles.contactNumberImageContainer}>
            <Picture
              imageCss={styles.contactNumberImage}
              image={ContactIcon}
              imageSizes={contactImageSizes}
              instantLoad
            />
          </div>
          <div>
            <h4 css={styles.contactNumberTitle}>
              {get(ContactPhrase, 'value')}
            </h4>
            <a href={`tel: ${phone}`} css={styles.contactNumberLink}>
              {phone}
            </a>
          </div>
        </div>
      </div>
    );
  }

  onFormSubmit = (fields, { formName, isModal }) => async (values, actions) => {
    try {
      await axios.post(`${API.PHONE_VALIDATION}${this.getPhoneParams(values)}`);
    } catch (error) {
      actions.setErrors({ PhoneNumber: this.t(dict.INVALID_PHONE) });
      this.trackFormSubmitError(error);

      return;
    }

    try {
      const mappedValues = this.mapValues(values, fields);
      const { Solution } = mappedValues;
      const { data } = await axios.post(
        API.ENQUIRY,
        setAntiForgeryToken(fields.AntiForgery, mappedValues),
      );
      const { id, createDate, requestedTourDate } = data;

      this.setState({ isThankyouVisible: true });
      this.trackFormSubmitSuccess({
        createDate,
        formName,
        id,
        isModal,
        requestedTourDate,
        Solution,
      });
    } catch (error) {
      actions.setStatus({ msg: this.t(dict.GENERAL_ERROR) });
      this.trackFormSubmitError(error);
    }
  };

  trackFormSubmitSuccess({
    createDate,
    formName,
    id,
    isModal,
    requestedTourDate,
    Solution,
  }) {
    setDataLayerObj({
      createDate,
      event: 'contactFormResponseObject',
      formName,
      formType: isModal ? 'modal' : 'page',
      id,
      requestedTourDate,
      solutionType: Solution,
      submitButton: 'submit',
    });
  }

  trackFormSubmitError(error) {
    setDataLayerObj({
      event: 'contactFormFailedSubmission',
      contactFormError: error.message,
      formId: FORM_ID,
    });
  }

  renderForm(fields, defaultFields) {
    const {
      SolutionsDropdownOptions,
      PrivacyPolicyLink,
      PhoneNumberOptions,
      DomainCountryISOCode2,
    } = fields;

    const { isDropDownOpen } = this.state;
    const { submitDictionary } = this.props;
    const { t } = this;
    const sessionStorageValues =
      typeof window !== 'undefined' ? window.sessionStorage : {};

    const defaultFieldsSource = isEmpty(defaultFields)
      ? sessionStorageValues
      : defaultFields;

    const initialValues = {
      ...defaultFieldsSource,
      Solution: getSolutionPrefilledValues(
        defaultFieldsSource,
        SolutionsDropdownOptions,
      ),
      FullName: '',
      Email: '',
      PhoneNumber: '',
      PrivacyPolicy: true,
    };

    const enquirySchema = object().shape({
      Solution: string().required(t(dict.REQUIRED_OPTION)),
      FullName: string().required(t(dict.REQUIRED_FIELD)),
      Email: string()
        .email(t(dict.INVALID_EMAIL))
        .required(t(dict.REQUIRED_FIELD)),
      PhoneNumber: string()
        .required(t(dict.REQUIRED_FIELD))
        .matches(REG_ONLY_NUMBERS, t(dict.INVALID_PHONE)),
    });

    const renderPrivacyPolicyLinkLabel = () => (
      <React.Fragment>
        <span css={styles.privacyLabel}>
          {t('PrivacyPolicyCheckboxAndText')}
        </span>
        <CtaLink cta={PrivacyPolicyLink} css={styles.checkbox} />
      </React.Fragment>
    );

    return (
      <Spacer marginCss={[styles.containerMargin, this.props.styles.container]}>
        <Container>
          <Row>
            <Column cols={{ lg: 6 }} offsets={{ lg: 1 }}>
              {this.renderFormHeadline(fields)}
            </Column>
          </Row>
          <Row>
            <Column
              cols={{ sm: 6, md: 7, lg: 5, xl: 5 }}
              offsets={{ lg: 1 }}
              css={styles.column}
            >
              <Formik
                initialValues={initialValues}
                validationSchema={enquirySchema}
                onSubmit={this.onFormSubmit(fields, this.props)}
                render={({
                  errors,
                  touched,
                  handleChange,
                  handleSubmit,
                  status,
                  values,
                }) => (
                  <form id={FORM_ID} onSubmit={handleSubmit}>
                    {!isEmpty(SolutionsDropdownOptions) && (
                      <Select
                        css={[styles.enquiryInput]}
                        {...styles.selectCssProps}
                        id="Solution"
                        disablePlaceholder
                        options={mapDropdownValues(
                          SolutionsDropdownOptions,
                          true,
                        )}
                        placeholder={decorateRequiredPlaceholder(
                          t('SolutionPlaceholder'),
                        )}
                        onChangeProp={(...args) => {
                          this.onSolutionsDropdownChange(...args);
                          handleChange(...args);
                        }}
                        error={errors.Solution && touched.Solution}
                        errorMessage={errors.Solution}
                        defaultValue={values.Solution.value}
                        defaultLabel={values.Solution.label}
                      />
                    )}
                    <Input
                      css={styles.enquiryInput}
                      {...styles.inputCssProps}
                      id="FullName"
                      placeholder={decorateRequiredPlaceholder(
                        t('FullNamePlaceholder'),
                      )}
                      onChange={handleChange}
                      error={errors.FullName && touched.FullName}
                      errorMessage={errors.FullName}
                      value={values.FullName}
                    />
                    <Input
                      css={styles.enquiryInput}
                      {...styles.inputCssProps}
                      id="Email"
                      placeholder={decorateRequiredPlaceholder(
                        t('EmailPlaceholder'),
                      )}
                      type="email"
                      onChange={handleChange}
                      error={errors.Email && touched.Email}
                      errorMessage={errors.Email}
                      value={values.Email}
                    />
                    <PhoneNumber
                      options={PhoneNumberOptions}
                      customCss={[styles.enquiryInput, styles.phoneNumberField]}
                      innerCss={styles.phoneNumberFieldCssProps}
                      id="PhoneNumber"
                      ref={this.phoneNumberRef}
                      defaultValue={DomainCountryISOCode2.value}
                      placeholder={decorateRequiredPlaceholder(
                        t('PhoneNumberPlaceholder'),
                      )}
                      onChange={handleChange}
                      error={errors.PhoneNumber && touched.PhoneNumber}
                      errorMessage={errors.PhoneNumber}
                      value={values.PhoneNumber}
                    />

                    <button
                      css={[
                        styles.tellUsMore,
                        this.shouldRender(values, SOLUTION_GROUPS.OTHER),
                      ]}
                      onClick={this.toggleDropDown}
                    >
                      {t('WouldYouLikeToTellUsMore')}
                      <Icon
                        name="chevron-down"
                        customCss={[
                          styles.downIcon,
                          isDropDownOpen && styles.chevronRotated,
                        ]}
                      />
                    </button>

                    {this.renderSecondForm(fields, handleChange, values)}

                    <div css={styles.checkboxWrapper}>
                      {status &&
                        status.msg &&
                        this.renderGeneralError(status.msg)}
                      <Checkbox
                        css={styles.checkbox}
                        id="SendMessages"
                        onChangeProp={handleChange}
                        label={t('CommunicationOfferCheckboxAndText')}
                        value={values.SendMessages}
                      />
                    </div>
                    <input
                      css={[
                        createButtonCss(),
                        styles.button,
                        styles.submitButton,
                      ]}
                      type="submit"
                      value={t(submitDictionary || defaultSubmitDictionary)}
                    />
                    {renderPrivacyPolicyLinkLabel()}
                  </form>
                )}
              />
            </Column>
            {this.renderRightSection(fields)}
          </Row>
        </Container>
      </Spacer>
    );
  }

  safeRender() {
    const { fields } = this;
    const { isModal, closeModal, defaultFields } = this.props;
    const { isThankyouVisible } = this.state;
    const ThankyouContent = () => (
      <p css={styles.thankyouDescription}>
        {this.t('ThankYouViewDescription')}
      </p>
    );

    return (
      <React.Fragment>
        <div css={isThankyouVisible ? modalFadeInCss : modalFadeOutCss}>
          <ThankYouForm {...fields} closeModal={closeModal} isModal={isModal}>
            <ThankyouContent />
          </ThankYouForm>
        </div>
        <div css={isThankyouVisible ? modalFadeOutCss : modalFadeInCss}>
          {this.renderForm(fields, defaultFields)}
        </div>
      </React.Fragment>
    );
  }
}

const stringPropShape = {
  value: PropTypes.string,
};

Enquiry.defaultProps = {
  closeModal: () => {},
  defaultFields: {},
  formName: 'enquiry',
  fields: defaultData,
  isModal: false,
  t: key => key,
  styles: {},
};

Enquiry.displayName = 'Enquiry';

Enquiry.propTypes = {
  closeModal: PropTypes.func,
  defaultFields: PropTypes.object,
  fields: PropTypes.shape({
    AntiForgery: PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.string,
    }),
    CompanySizeOptions: PropTypes.array,
    ContactIcon: PropTypes.shape({
      value: PropTypes.shape({}),
    }),
    ContactPhrasex: PropTypes.string,
    ContactPhrase: PropTypes.shape({
      value: PropTypes.string,
    }),
    DomainCountryISOCode2: PropTypes.shape(stringPropShape),
    EnquiryHeadlineSubtitle: PropTypes.shape(stringPropShape),
    ForHowLongOptions: PropTypes.array,
    Headline: PropTypes.shape(stringPropShape),
    HowManyPeopleOptions: PropTypes.array,
    InformationPhrases: PropTypes.shape({
      value: PropTypes.array,
    }),
    LocalPhoneNumber: PropTypes.shape(stringPropShape),
    LocationID: PropTypes.shape(stringPropShape),
    MoveInDateOptions: PropTypes.array,
    PhoneNumberOptions: PropTypes.array,
    PrefilledSolutionId: PropTypes.shape(stringPropShape),
    PrivacyPolicyLink: PropTypes.object,
    SolutionsDropdownOptions: PropTypes.array,
    ThankYouViewImage: PropTypes.shape({
      value: PropTypes.shape({ alt: PropTypes.string, src: PropTypes.string }),
    }),
  }),
  formName: PropTypes.string,
  isModal: PropTypes.bool,
  t: PropTypes.func,
  headlineDictionary: PropTypes.string,
  submitDictionary: PropTypes.string,
  hideGoBack: PropTypes.bool,
  styles: PropTypes.any,
};

export default compose(withNamespaces(), withSitecoreContext())(Enquiry);
